import axios from 'axios';

// A configured URL pointing at localhost is only right for the machine running the API. When the
// page is opened from another PC on the network (http://192.168.1.12:3001) that same URL would
// send the browser to *its own* machine, so the host is swapped for the one the page came from —
// the API is on the same box as the dev server. An explicitly non-local URL is always honoured.
const resolveApiUrl = () => {
  const configured = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  if (typeof window === 'undefined') return configured;

  const pageHost = window.location.hostname;
  const isLocal = (h) => h === 'localhost' || h === '127.0.0.1' || h === '[::1]';
  if (isLocal(pageHost)) return configured;

  try {
    const url = new URL(configured, window.location.origin);
    if (!isLocal(url.hostname)) return configured;
    url.hostname = pageHost;
    url.protocol = window.location.protocol;
    return url.toString().replace(/\/$/, '');
  } catch {
    return configured;
  }
};

const API_URL = resolveApiUrl();

const client = axios.create({ baseURL: API_URL });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('company');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default client;

export const getApiError = (err, fallback = 'Gabim i panjohur.') => {
  const d = err?.response?.data;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (d.errors) {
    const msgs = Object.values(d.errors).flat();
    if (msgs.length) return msgs.join(', ');
  }
  return d.title || d.message || fallback;
};

// Bare client for unauthenticated auth calls so the global 401 redirect doesn't hijack
// the confirm-email / resend flows (an invalid token legitimately returns 401).
const authHttp = axios.create({ baseURL: API_URL });

export const auth = {
  register: (data) => client.post('/auth/register', data),
  login: (data) => client.post('/auth/login', data),
  changePassword: (data) => client.post('/auth/change-password', data),
  confirmEmail: (token) => authHttp.post('/auth/confirm-email', { token }),
  resendConfirmation: (email) => authHttp.post('/auth/resend-confirmation', { email }),
  forgotPassword: (email) => authHttp.post('/auth/forgot-password', { email }),
  resetPassword: (data) => authHttp.post('/auth/reset-password', data),
};

export const company = {
  get: () => client.get('/company'),
  update: (data) => client.put('/company', data),
};

export const terms = {
  get: () => client.get('/terms'),
  accept: () => client.post('/terms/accept'),
};

export const products = {
  getAll: (params) => client.get('/products', { params }),
  getById: (id, params) => client.get(`/products/${id}`, { params }),
  barcodeCheck: (barcode) => client.get('/products/barcode-check', { params: { barcode } }),
  create: (data) => client.post('/products', data),
  update: (id, data) => client.put(`/products/${id}`, data),
  delete: (id) => client.delete(`/products/${id}`),
  adjustStock: (id, data) => client.post(`/products/${id}/adjust-stock`, data),
  getMovements: (id, params) => client.get(`/products/${id}/movements`, { params }),
  setECDeclaration: (id, data) => client.post(`/products/${id}/ec-declaration`, data),
  uploadECDocument: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return client.post(`/products/${id}/ec-declaration/upload`, fd);
  },
  uploadFile: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return client.post(`/products/${id}/files`, fd);
  },
  deleteFile: (fileId) => client.delete(`/products/files/${fileId}`),
  getCategories: () => client.get('/products/categories'),
  // Bulk import from a spreadsheet: read the file first (nothing is written), then send back the
  // rows the office accepted. See api/Services/ProductImportService.cs.
  importPreview: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return client.post('/products/import/preview', fd);
  },
  importCommit: (data) => client.post('/products/import', data),
  importTemplate: () => client.get('/products/import/template', { responseType: 'blob' }),
  createCategory: (data) => client.post('/products/categories', data),
  qr: (id) => client.get(`/products/${id}/qr`, { responseType: 'blob' }),
};

export const branches = {
  getAll: () => client.get('/branches'),
  getById: (id) => client.get(`/branches/${id}`),
  create: (data) => client.post('/branches', data),
  update: (id, data) => client.put(`/branches/${id}`, data),
  delete: (id) => client.delete(`/branches/${id}`),
};

export const workers = {
  getAll: () => client.get('/workers'),
  invite: (data) => client.post('/workers', data),
  update: (id, data) => client.put(`/workers/${id}`, data),
  remove: (id) => client.delete(`/workers/${id}`),
};

export const subscription = {
  getCurrent: () => client.get('/subscription'),
  setup: ({ successUrl, cancelUrl }) => client.post('/subscription/setup', { successUrl, cancelUrl }),
  verify: (sessionId) => client.post(`/subscription/verify?sessionId=${sessionId}`),
  // { billingInterval: 'Monthly' | 'Yearly', storageGBQuota }
  updatePlan: (data) => client.put('/subscription/plan', data),
  setAddon: (enabled) => client.post('/subscription/addon', { enabled }),
  // Fiscalization (SEF) is its own +€10/mo line.
  setFiscalizationAddon: (enabled) => client.post('/subscription/addon/fiscalization', { enabled }),
  cancel: () => client.post('/subscription/cancel'),
  payments: () => client.get('/subscription/payments'),
  paymentInvoicePdf: (invoiceId) => client.get(`/subscription/payments/${invoiceId}/invoice-pdf`, { responseType: 'blob' }),
};

export const invoiceScan = {
  scan: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return client.post('/invoice-scan/scan', fd);
  },
  createProducts: (data) => client.post('/invoice-scan/create-products', data),
};

export const ecDeclarations = {
  // branchId optional; productIds optional — when provided, only those products are exported.
  exportPdf: (branchId, productIds) => client.post('/ec-declarations/export/pdf',
    { branchId: branchId || null, productIds: productIds || null }, { responseType: 'blob' }),
  exportCsv: (branchId, productIds) => client.post('/ec-declarations/export/csv',
    { branchId: branchId || null, productIds: productIds || null }, { responseType: 'blob' }),
  exportPackage: (branchId, productIds) => client.post('/ec-declarations/export/package',
    { branchId: branchId || null, productIds: productIds || null }, { responseType: 'blob' }),
};

export const catalog = {
  // clientId optional — when set, the public link pre-fills that client's details.
  share: (branchId, clientId) => client.post('/catalog/share', { branchId, clientId: clientId || null }),
};

export const orders = {
  getAll: (params) => client.get('/orders', { params }),
  getById: (id) => client.get(`/orders/${id}`),
  newCount: () => client.get('/orders/new-count'),
  approve: (id) => client.post(`/orders/${id}/approve`),
  reject: (id) => client.post(`/orders/${id}/reject`),
};

export const webPush = {
  vapidPublicKey: () => client.get('/devices/web-push/vapid-public-key'),
  register: (data) => client.post('/devices/web-push/register', data),
  unregister: (data) => client.post('/devices/web-push/unregister', data),
};

export const notifications = {
  getAll: (params) => client.get('/notifications', { params }),
  unreadCount: () => client.get('/notifications/unread-count'),
  markRead: (id) => client.post(`/notifications/${id}/read`),
  markAllRead: () => client.post('/notifications/read-all'),
};

// Bare client for the public catalog — no auth header, no 401 redirect.
const publicHttp = axios.create({ baseURL: API_URL });
export const publicCatalog = {
  get: (token) => publicHttp.get(`/public/catalog/${token}`),
  order: (token, data) => publicHttp.post(`/public/catalog/${token}/order`, data),
};

export const clients = {
  getAll: () => client.get('/clients'),
  getById: (id) => client.get(`/clients/${id}`),
  create: (data) => client.post('/clients', data),
  update: (id, data) => client.put(`/clients/${id}`, data),
  delete: (id) => client.delete(`/clients/${id}`),
};

// Platform-operator dashboard (super admin only — gated server-side on the isPlatformAdmin claim).
export const admin = {
  overview: () => client.get('/admin/overview'),
  companies: () => client.get('/admin/companies'),
};

// SEF — fiscalization against the ATK (Tax Administration of Kosovo) service.
export const fiscalization = {
  status: () => client.get('/fiscalization/status'),
  getConfig: () => client.get('/fiscalization/config'),
  updateConfig: (data) => client.put('/fiscalization/config', data),

  getPos: () => client.get('/fiscalization/pos'),
  createPos: (data) => client.post('/fiscalization/pos', data),
  onboardPos: (id) => client.post(`/fiscalization/pos/${id}/onboard`),
  // Reserves the next block of coupon numbers — what lets a till keep selling offline.
  reserveCouponBlock: (id) => client.post(`/fiscalization/pos/${id}/coupon-block`),
  deletePos: (id) => client.delete(`/fiscalization/pos/${id}`),

  getCoupons: (params) => client.get('/fiscalization/coupons', { params }),
  getCoupon: (id) => client.get(`/fiscalization/coupons/${id}`),
  issue: (data) => client.post('/fiscalization/coupons', data),
  fiscalizeInvoice: (invoiceId, data) => client.post(`/fiscalization/invoices/${invoiceId}/fiscalize`, data),
  cancel: (id, data) => client.post(`/fiscalization/coupons/${id}/cancel`, data),
  returnCoupon: (id, data) => client.post(`/fiscalization/coupons/${id}/return`, data),
  retry: (id) => client.post(`/fiscalization/coupons/${id}/retry`),
  couponPdf: (id) => client.get(`/fiscalization/coupons/${id}/pdf`, { responseType: 'blob' }),
  // Blob, not a URL: the endpoint is authenticated, so an <img src> would be rejected.
  couponQr: (id) => client.get(`/fiscalization/coupons/${id}/qr`, { responseType: 'blob' }),
  // format: 'csv' | 'pdf'. onlyUntransmitted defaults to the data ATK asks for.
  export: (params) => client.get('/fiscalization/export', { params, responseType: 'blob' }),
};

// On-premise install: the till's own setup/sync state. `status` is deliberately on a bare client
// — it is called before login, on a machine that may have no users yet.
const posHttp = axios.create({ baseURL: API_URL });
export const posSetup = {
  status: () => posHttp.get('/pos-setup/status'),
  // `serverUrl` is the operator's override and is normally empty: the address travels inside the
  // activation key. It exists for the PC where both the key and the packaged address are wrong.
  activate: (activationCode, serverUrl) =>
    posHttp.post('/pos-setup/activate', { activationCode, serverUrl: serverUrl || null }),
  syncNow: () => client.post('/pos-setup/sync'),
  // Pulls the whole catalogue rather than the changes since last time — the way out of a till
  // whose product list has drifted from the central one.
  resync: () => client.post('/pos-setup/resync'),
  reset: () => client.post('/pos-setup/reset'),
};

// Back office: which PCs are allowed to sync, one per branch.
export const posInstallations = {
  getAll: () => client.get('/pos-sync/installations'),
  create: (data) => client.post('/pos-sync/installations', data),
  // A fresh one-time code for an installation that already exists — the original is shown once and
  // never stored in the clear, so this is how a mislaid code or a replaced PC is dealt with without
  // discarding the installation and the branch history a till seeds from.
  reissueCode: (id) => client.post(`/pos-sync/installations/${id}/activation-code`),
  revoke: (id) => client.post(`/pos-sync/installations/${id}/revoke`),
  // The desktop application itself. `installerInfo` is ungated so the UI can offer the add-on;
  // the download 402s without it (Filters/RequiresFiscalizationAddonAttribute).
  installerInfo: () => client.get('/pos-sync/installer/info'),
  downloadInstaller: (onProgress) => client.get('/pos-sync/installer', {
    responseType: 'blob',
    onDownloadProgress: onProgress,
    // ~80 MB over a shop line: the global 30s-ish expectations do not apply.
    timeout: 0,
  }),
};

export const invoices = {
  getAll: (params) => client.get('/invoices', { params }),
  getById: (id) => client.get(`/invoices/${id}`),
  create: (data) => client.post('/invoices', data),
  update: (id, data) => client.put(`/invoices/${id}`, data),
  delete: (id) => client.delete(`/invoices/${id}`),
  updateStatus: (id, status) => client.patch(`/invoices/${id}/status`, { status }),
  getPdf: (id) => client.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  getBankInfo: () => client.get('/invoices/bank-info'),
  updateBankInfo: (data) => client.put('/invoices/bank-info', data),
};
